// react
import React, { useEffect, useState, useCallback, useRef } from "react";
// entities
import { AppOnboardingVideo } from "@/features/auth/types/auth";
import {
  useGetAppOnboardingVideoScreensQuery,
  useGetSavedObScreensQuery,
} from "@/features/auth/api/authAPI";
import Spinner from "@/shared/ui/Spinner/Spinner";
import styles from "./WebOnboardingHomePage.module.scss";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { getZoomState } from "@/entities/zoom";
import { useInView } from "react-intersection-observer";
import { useCurrentOrganization, useMe } from "@/Hooks";
import { RightClickModal } from "@/shared/ui/RightClickModal/RightClickModal";
import ContentLoader from "react-content-loader";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import InfoPhoneHomePageHorizontal from "@/shared/ui/InfoPhoneHomePageHorizontal/InfoPhoneHomePageHorizontal";
import { useSelectedOnboardingScreens } from "@/entities/selectedScreens";
import { VideoLinkProvider } from "@/app/providers/video/VideoLinkProvider";

interface WebOnboardingHomePageProps {
  videoData: AppOnboardingVideo;
  isOpen: boolean;
  savedScreensMap: Record<
    number,
    { is_saved: boolean; is_saved_pk: number | null }
  >;
}

const WebOnboardingHomePage: React.FC<WebOnboardingHomePageProps> = ({
  videoData,
  isOpen,
  savedScreensMap,
}) => {
  // Move all hooks to the top
  //const [parent] = useAutoAnimate();
  const [page, setPage] = useState(1);
  const [allScreens, setAllScreens] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | undefined>(
    undefined,
  );
  const [imageIndex, setImageIndex] = useState<number | null>(null);
  const [currentModalImage, setCurrentModalImage] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { zoom } = useSelector(getZoomState);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const { ref: refIntersection, inView } = useInView({
    threshold: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const currentOrg = useCurrentOrganization();
  const { me } = useMe();
  const [savingStates, setSavingStates] = useState<Record<string, boolean>>({});
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    show: boolean;
    imageId: string | null;
    timestamp?: number;
    imageUrl?: string;
  }>({
    x: 0,
    y: 0,
    show: false,
    imageId: null,
  });

  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = useSelectedOnboardingScreens();

  const [isSelected, setIsSelected] = useState(false);
  const [modalImageSelected, setModalImageSelected] = useState(false);
  const [hoveredImageId, setHoveredImageId] = useState<number | null>(null);
  const [modalHovered, setModalHovered] = useState(false);

  const appInfoData = [
    {
      img: videoData.store_url,
      title: "Web onboarding page:",
      text: "Click me to see",
      onClick: () => window.open(videoData.store_url, "_blank"),
    },
    {
      img: videoData.app.icon,
      title: "Monthly Revenue:",
      text: videoData.app.revenue
        ? videoData.app.revenue < 5000
          ? "<5k"
          : `$${videoData.app.revenue}`
        : null,
    },
    {
      img: videoData.app.icon,
      title: "Monthly Installs:",
      text: videoData.app.downloads
        ? videoData.app.downloads < 5000
          ? "<5k"
          : `${videoData.app.downloads}`
        : null,
    },
  ].filter((info) => info.text !== null);

  const {
    data: screenData,
    isLoading: isScreensLoading,
    isFetching: isScreensFetching,
  } = useGetAppOnboardingVideoScreensQuery(
    {
      app_ob_video: videoData.id,
      page,
      page_size: 10,
    },
    {
      skip: !hasMore,
    },
  );

  const { data: savedObScreens, refetch: refetchSavedObScreens } =
    useGetSavedObScreensQuery(
      {
        organizationUuid: currentOrg?.uuid || "",
      },
      {
        skip: !currentOrg?.uuid,
      },
    );

  // All useEffect hooks
  useEffect(() => {
    if (screenData?.results) {
      setAllScreens((prev) => [...prev, ...screenData.results]);
      setHasMore(!!screenData.next);
    }
  }, [screenData]);

  useEffect(() => {
    return () => {
      setAllScreens([]);
      setPage(1);
    };
  }, [videoData.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage) {
        if (event.key === "ArrowRight") {
          handleNextImage();
        } else if (event.key === "ArrowLeft") {
          handlePrevImage();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage]);

  useEffect(() => {
    if (isLoading && screenData?.results) {
      setIsLoading(false);
      if (imageIndex !== null) {
        const nextIndex = imageIndex + 1;
        if (nextIndex < allScreens.length) {
          const nextImage = allScreens[nextIndex];
          setSelectedImage(nextImage.screen);
          setImageIndex(nextIndex);
          setCurrentModalImage(nextImage);
        }
      }
    }
  }, [screenData, allScreens, imageIndex, isLoading]);

  // Update allScreens when savedScreensMap changes
  useEffect(() => {
    if (Object.keys(savedScreensMap).length > 0) {
      setAllScreens((prevScreens) => {
        const updatedScreens = prevScreens.map((screen) => {
          if (savedScreensMap[screen.id]) {
            return {
              ...screen,
              is_saved: savedScreensMap[screen.id].is_saved,
              is_saved_pk: savedScreensMap[screen.id].is_saved_pk,
            };
          }
          return screen;
        });
        return updatedScreens;
      });
    }
  }, [savedScreensMap]);

  // All handler functions
  const loadMore = useCallback(() => {
    if (hasMore && !isScreensFetching) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, isScreensFetching]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasMore || isScreensFetching) return;

    const container = containerRef.current;
    const scrollPosition = container.scrollTop + container.clientHeight;
    const threshold = container.scrollHeight - 100; // Load more when 100px from bottom

    if (scrollPosition >= threshold) {
      loadMore();
    }
  }, [hasMore, isScreensFetching, loadMore]);

  const closeImageModal = () => {
    setSelectedImage(undefined);
    setImageIndex(null);
    setCurrentModalImage(null);
  };

  const handleNextImage = () => {
    if (imageIndex !== null && allScreens) {
      const nextIndex = imageIndex + 1;

      if (nextIndex >= allScreens.length) {
        if (screenData?.next) {
          setIsLoading(true);
          setPage((prev) => prev + 1);
          return;
        } else {
          setImageIndex(0);
          setSelectedImage(allScreens[0].screen);
          setCurrentModalImage(allScreens[0]);
          return;
        }
      }

      const nextImage = allScreens[nextIndex];
      setSelectedImage(nextImage.screen);
      setImageIndex(nextIndex);
      setCurrentModalImage(nextImage);
    }
  };

  const handlePrevImage = () => {
    if (imageIndex !== null && allScreens) {
      const prevIndex =
        (imageIndex - 1 + allScreens.length) % allScreens.length;
      const prevImage = allScreens[prevIndex];
      setSelectedImage(prevImage.screen);
      setImageIndex(prevIndex);
      setCurrentModalImage(prevImage);
    }
  };

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isScreensFetching) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      },
    );

    if (spinnerRef.current) {
      observer.observe(spinnerRef.current);
    }

    return () => {
      if (spinnerRef.current) {
        observer.unobserve(spinnerRef.current);
      }
    };
  }, [loadMore, hasMore, isScreensFetching]);

  // Add scroll listener when zoom > 1
  useEffect(() => {
    const container = containerRef.current;
    if (zoom && zoom > 1 && container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [zoom, handleScroll]);

  // Load more when element comes into view
  useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);

  const handleVideoClick = (timestamp: number) => {
    handleOpenVideoPage(
      timestamp,
      videoData.app?.slug,
      "Web Onboarding Page screens",
      2,
      videoData.app?.id,
      undefined,
      videoData.id,
      me?.username || "",
      currentOrg?.uuid || "",
    );
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      show: true,
      imageId: imageId,
      timestamp: timestamp,
      imageUrl: imageUrl,
    });
  };

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, show: false }));
  }, []);

  // Handle selection for the main image view
  const handleSelectImage = (e: React.MouseEvent, screen: any) => {
    e.stopPropagation();

    // Get the latest saved status from savedScreensMap if available
    const isSaved = savedScreensMap[screen.id]
      ? savedScreensMap[screen.id].is_saved
      : screen.is_saved;

    const savedPk = savedScreensMap[screen.id]
      ? savedScreensMap[screen.id].is_saved_pk
      : screen.is_saved_pk;

    if (checkIsSelected(screen.id)) {
      removeScreen(screen.id);
      setIsSelected(false);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === screen.id) {
        setModalImageSelected(false);
      }
    } else {
      addScreen({
        id: screen.id,
        is_saved: isSaved,
        is_saved_pk: savedPk,
        timestamp: screen.timestamp,
        app_video: videoData.id,
      });
      setIsSelected(true);
      // If the modal is open and showing this image, update modal selection state too
      if (currentModalImage && currentModalImage.id === screen.id) {
        setModalImageSelected(true);
      }
    }
  };

  // Add this function to handle selection in the modal
  const handleModalSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentModalImage) return;

    if (checkIsSelected(currentModalImage.id)) {
      removeScreen(currentModalImage.id);
      setModalImageSelected(false);
      // If the modal image is the same as the main image, update main image selection state too
      if (
        currentModalImage.id === imageIndex &&
        imageIndex !== null &&
        allScreens[imageIndex]
      ) {
        setIsSelected(false);
      }
    } else {
      addScreen({
        id: currentModalImage.id,
        is_saved: currentModalImage.is_saved,
        is_saved_pk: currentModalImage.is_saved_pk,
        timestamp: currentModalImage.timestamp,
        app_video: videoData.id,
      });
      setModalImageSelected(true);
      // If the modal image is the same as the main image, update main image selection state too
      if (
        currentModalImage.id === imageIndex &&
        imageIndex !== null &&
        allScreens[imageIndex]
      ) {
        setIsSelected(true);
      }
    }
  };

  // Add this useEffect to update both selection states when selectedScreens changes
  useEffect(() => {
    // Update modal image selection state if modal is open
    if (currentModalImage) {
      setModalImageSelected(checkIsSelected(currentModalImage.id));
    }
  }, [selectedScreens, currentModalImage, checkIsSelected]);

  if (screenData?.count === 0) {
    return null;
  }

  return (
    <div className={styles.root} ref={containerRef}>
      <div className={styles.container} style={{ zoom: zoom }}>
        <div className={styles.appInfo}>
          <VideoLinkProvider
            timestamp={0}
            slugText={videoData.app?.slug}
            from="Web Onboarding Page screens"
            videoType={2}
            appId={videoData.app?.id}
            onboardingVideoId={videoData.id}
            userName={me?.username || ""}
            organizationId={currentOrg?.uuid || ""}
          >
            <div className={styles.appInfoLeft}>
              {videoData.app && (
                <>
                  <img
                    src={videoData.app.icon}
                    alt={videoData.app.name}
                    className={styles.appIcon}
                  />
                  <div className={styles.appDetails}>
                    <h3>{videoData.app.name}</h3>
                    <p>{videoData.app.shortname}</p>
                  </div>
                </>
              )}
            </div>
          </VideoLinkProvider>
          <div className={styles.appInfoRight}>
            {appInfoData.map((info, index) => (
              <InfoPhoneHomePageHorizontal
                key={index}
                img={info.img}
                title={info.title}
                text={info.text}
                onClick={info.onClick}
              />
            ))}
          </div>
        </div>
        <div className={styles.screensGrid} style={{ zoom: zoom }}>
          {isScreensLoading && allScreens.length === 0 ? (
            <div className={styles.loading}>
              <Spinner />
            </div>
          ) : (
            <>
              {allScreens.map((screen) => {
                // Get the saved status directly from savedScreensMap if available, otherwise use screen's own status
                const isSaved = savedScreensMap[screen.id]
                  ? savedScreensMap[screen.id].is_saved
                  : screen.is_saved;

                const savedPk = savedScreensMap[screen.id]
                  ? savedScreensMap[screen.id].is_saved_pk
                  : screen.is_saved_pk;

                const isSaving = savingStates[screen.timestamp] || false;
                const isScreenSelected = checkIsSelected(screen.id);

                return (
                  <div
                    key={screen.id}
                    className={styles.screenItem}
                    onMouseEnter={() => setHoveredImageId(screen.id)}
                    onMouseLeave={() => setHoveredImageId(null)}
                    onContextMenu={(e) =>
                      handleContextMenu(
                        e,
                        screen.id.toString(),
                        Number(screen.timestamp),
                        screen.screen,
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(screen.screen);
                      setImageIndex(
                        allScreens.findIndex((s) => s.id === screen.id),
                      );
                      setCurrentModalImage({
                        ...screen,
                        is_saved: isSaved,
                        is_saved_pk: savedPk,
                      });
                      setModalImageSelected(checkIsSelected(screen.id));
                    }}
                  >
                    <img
                      src={screen.screen}
                      alt={`Screen ${screen.id}`}
                      className={styles.screenImage}
                    />

                    {(hoveredImageId === screen.id || isScreenSelected) && (
                      <div className={styles.selectButtonContainer}>
                        <div className={styles.selectButtonContainerInner}>
                          {hoveredImageId === screen.id && isSaved && (
                            <div className={styles.savedTag}>
                              <p>Saved</p>
                            </div>
                          )}

                          <button
                            className={`${styles.selectButton} ${isScreenSelected ? styles.selected : ""}`}
                            onClick={(e) =>
                              handleSelectImage(e, {
                                ...screen,
                                is_saved: isSaved,
                                is_saved_pk: savedPk,
                              })
                            }
                          >
                            {isScreenSelected && (
                              <img
                                src="/img/blackIcon/Check.svg"
                                alt="selectImage"
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {hoveredImageId === screen.id && (
                      <div className={styles.bottomMenuContainer}>
                        <div className={styles.bottomMenuContainerInner}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVideoClick(screen.timestamp);
                            }}
                          >
                            <img
                              src="/img/see-video-icon.svg"
                              alt="see-video"
                            />
                            <p>See Video</p>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(isScreensFetching || hasMore) && (
                <div ref={spinnerRef} className={styles.loadingMore}>
                  <Spinner />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        isOpen={!!selectedImage}
        onRequestClose={closeImageModal}
        contentLabel="Image Modal"
        className={styles.modal}
        overlayClassName={styles.overlay}
      >
        <div className={styles.modalContent}>
          <div
            className={styles.containerImage}
            onMouseEnter={() => setModalHovered(true)}
            onMouseLeave={() => setModalHovered(false)}
          >
            {isLoading ? (
              <ContentLoader
                speed={2}
                width={400}
                height={800}
                viewBox="0 0 400 800"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                <rect x="0" y="0" rx="2" ry="2" width="400" height="800" />
              </ContentLoader>
            ) : (
              <div className={styles.modalContentPhotoContainer}>
                <img
                  className={styles.modalContentPhoto}
                  src={selectedImage!}
                  alt="Enlarged pic"
                  loading="lazy"
                />

                {(modalHovered || modalImageSelected) && (
                  <div className={styles.modalSelectButtonContainer}>
                    <div className={styles.modalSelectButtonContainerInner}>
                      {modalHovered &&
                        currentModalImage &&
                        currentModalImage.is_saved && (
                          <div className={styles.savedTag}>
                            <p>Saved</p>
                          </div>
                        )}

                      <button
                        className={`${styles.selectButton} ${modalImageSelected ? styles.selected : ""}`}
                        onClick={handleModalSelectImage}
                      >
                        {modalImageSelected && (
                          <img
                            src="/img/blackIcon/Check.svg"
                            alt="selectImage"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {modalHovered && (
                  <div className={styles.modalBottomMenuContainer}>
                    <div className={styles.modalBottomMenuContainerInner}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoClick(currentModalImage?.timestamp || 0);
                        }}
                      >
                        <img src="/img/see-video-icon.svg" alt="see-video" />
                        <p>See Video</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <img
            className={styles.arrowRight}
            src="/img/whiteIcon/ChevronRight.svg"
            alt="arrowRightImg"
            onClick={handleNextImage}
            loading="lazy"
            style={{
              opacity:
                imageIndex === allScreens.length - 1 && !screenData?.next
                  ? 0.5
                  : 1,
            }}
          />
          <img
            className={styles.arrowLeft}
            src="/img/whiteIcon/ChevronLeft.svg"
            alt="arrowLeftImg"
            onClick={handlePrevImage}
            loading="lazy"
          />
        </div>
      </Modal>
      {contextMenu.show && (
        <RightClickModal
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          imageUrl={contextMenu.imageUrl || ""}
          imageId={contextMenu.imageId || ""}
          handleOpenInNewTab={(timestamp) => handleVideoClick(timestamp)}
          timestamp={contextMenu.timestamp || 0}
          avsID={videoData.id}
          refetchData={refetchSavedObScreens}
          imageType="webOnboarding"
          onlyCopy
        />
      )}
    </div>
  );
};

export default WebOnboardingHomePage;
